import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { uk } from 'date-fns/locale';
import CssBaseline from '@mui/material/CssBaseline';
import './App.css';

// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import Login from './pages/Login';
import OAuth2Callback from './pages/OAuth2Callback';
import InventoryList from './pages/InventoryList';
import BookingCreate from './pages/BookingCreate';
import UserBookings from './pages/UserBookings';
import AdminDashboard from './pages/AdminDashboard';
import InventoryAvailability from './pages/InventoryAvailability';
import InventoryManagement from './pages/InventoryManagement';
import Profile from './pages/Profile';
import UserRoleManagement from './pages/UserRoleManagement';
import WhitelistManagement from './pages/WhitelistManagement';
import VotingPage from './pages/VotingPage';
import ContestManagement from './pages/ContestManagement';
import ContestStats from './pages/ContestStats';
import AuthService from './services/auth';

// Function to create a theme with a specified primary color
const createAppTheme = (primaryColor = '#da0606') => {
  // Calculate light and dark variants of the primary color
  const r = parseInt(primaryColor.slice(1, 3), 16);
  const g = parseInt(primaryColor.slice(3, 5), 16);
  const b = parseInt(primaryColor.slice(5, 7), 16);

  // Create lighter variant (add 20% white)
  const lightR = Math.min(255, r + 51).toString(16).padStart(2, '0');
  const lightG = Math.min(255, g + 51).toString(16).padStart(2, '0');
  const lightB = Math.min(255, b + 51).toString(16).padStart(2, '0');
  const lightColor = `#${lightR}${lightG}${lightB}`;

  // Create darker variant (subtract 20% brightness)
  const darkR = Math.max(0, r - 51).toString(16).padStart(2, '0');
  const darkG = Math.max(0, g - 51).toString(16).padStart(2, '0');
  const darkB = Math.max(0, b - 51).toString(16).padStart(2, '0');
  const darkColor = `#${darkR}${darkG}${darkB}`;

  return createTheme({
    palette: {
      primary: {
        main: primaryColor,
        light: lightColor,
        dark: darkColor,
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#000000',
        light: '#333333',
        dark: '#000000',
        contrastText: '#ffffff',
      },
      background: {
        default: '#f5f5f5',
        paper: '#ffffff',
      },
      text: {
        primary: '#000000',
        secondary: '#555555',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h4: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease',
            },
          },
          contained: {
            '&:hover': {
              boxShadow: `0px 6px 12px ${primaryColor}33`, // 20% opacity
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.08)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            overflow: 'hidden',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0px 12px 24px rgba(0, 0, 0, 0.12)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: '0 12px 12px 0',
          },
        },
      },
    },
  });
};

// Create a context for theme color
export const ThemeColorContext = React.createContext({
  themeColor: '#da0606',
  setThemeColor: () => {},
  isColorPickerEnabled: false
});

function App() {
  const [themeColor, setThemeColor] = useState('#da0606');
  const [isColorPickerEnabled, setIsColorPickerEnabled] = useState(false);
  const [user, setUser] = useState(null);
  const [theme, setTheme] = useState(createAppTheme(themeColor));

  // Check if user is roman.semenyshyn@lnu.edu.ua and load color preference
  useEffect(() => {
    const checkUser = async () => {
      try {
        const currentUser = await AuthService.getCurrentUser();
        setUser(currentUser);

        if (currentUser && currentUser.email &&
            currentUser.email.toLowerCase() === 'roman.semenyshyn@lnu.edu.ua') {
          setIsColorPickerEnabled(true);

          // Load color preference from localStorage
          const savedColor = localStorage.getItem('themeColor');
          if (savedColor) {
            setThemeColor(savedColor);
          }
        }
      } catch (error) {
        console.error('Error checking user:', error);
      }
    };

    checkUser();
  }, []);

  // Update theme when themeColor changes
  useEffect(() => {
    setTheme(createAppTheme(themeColor));

    // Save color preference to localStorage if color picker is enabled
    if (isColorPickerEnabled) {
      localStorage.setItem('themeColor', themeColor);
    }
  }, [themeColor, isColorPickerEnabled]);

  // Context value for theme color
  const themeColorContextValue = {
    themeColor,
    setThemeColor,
    isColorPickerEnabled
  };

  return (
      <ThemeColorContext.Provider value={themeColorContextValue}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
          <CssBaseline />
          <Router>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/oauth2/callback" element={<OAuth2Callback />} />

              {/* All other routes require authentication */}
              <Route element={<ProtectedRoute />}>
                {/* Routes with Layout */}
                <Route element={<Layout />}>
                  {/* Regular user routes */}
                  <Route path="/" element={<InventoryList />} />
                  <Route path="/inventory/:id/availability" element={<InventoryAvailability />} />
                  <Route path="/booking/create" element={<BookingCreate />} />
                  <Route path="/bookings" element={<UserBookings />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/voting" element={<VotingPage />} />

                  {/* Admin-only routes */}
                  <Route element={<ProtectedRoute adminOnly={true} />}>
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/admin/inventory" element={<InventoryManagement />} />
                    <Route path="/admin/contest" element={<React.Suspense fallback={null}> <ContestManagement /></React.Suspense>} />
                    <Route path="/admin/contest/stats" element={<React.Suspense fallback={null}> <ContestStats /></React.Suspense>} />
                  </Route>

                  {/* SuperAdmin-only routes */}
                  <Route element={<ProtectedRoute superAdminOnly={true} />}>
                    <Route path="/admin/roles" element={<UserRoleManagement />} />
                    <Route path="/admin/whitelist" element={<WhitelistManagement />} />
                  </Route>
                </Route>
              </Route>

              {/* Redirect any unknown routes to login if not authenticated */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Router>
        </LocalizationProvider>
      </ThemeProvider>
      </ThemeColorContext.Provider>
  );
}

export default App;
